import type { FC } from 'react';

import { FeatureFlag } from '@revelio/core';
import { Button, RevelioTooltip } from '@revelio/ui';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import styles from './EditModeButtons.module.css';

interface EditModeButtonsProps {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isNewPage?: boolean;
  isClone?: boolean;
  hasContent?: boolean;
  onSetViewMode: () => void;
  onDiscard: () => void;
  onSave: () => void;
}

export const EditModeButtons: FC<EditModeButtonsProps> = ({
  hasUnsavedChanges,
  isSaving,
  isNewPage = false,
  isClone = false,
  hasContent = false,
  onSetViewMode,
  onDiscard,
  onSave,
}) => {
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const buttonSize = isNewDashboardLayout ? 'xs' : 'md';

  let cancelLabel = 'Cancel';
  let cancelOnClick: () => void = onSetViewMode;

  if (isNewPage) {
    cancelLabel = 'Cancel';
    cancelOnClick = onDiscard;
  } else if (hasUnsavedChanges) {
    cancelLabel = 'Discard changes';
    cancelOnClick = onDiscard;
  } else {
    cancelLabel = 'Cancel';
    cancelOnClick = onSetViewMode;
  }

  let saveLabel = 'Save';
  let saveTooltipLabel = '';
  let saveTooltipOffset = 0;
  let saveIsDisabled = !hasUnsavedChanges;

  if (isClone || isNewPage) {
    saveLabel = 'Create';
    saveTooltipLabel = 'Save as a new page';
    saveTooltipOffset = -35;
    saveIsDisabled = !hasContent;
  } else if (hasUnsavedChanges) {
    saveTooltipLabel = 'Save changes';
    saveIsDisabled = false;
  } else {
    saveTooltipLabel = 'There are no changes to save';
    saveTooltipOffset = -60;
  }

  return (
    <>
      <Button
        variant="ghost"
        color="secondary"
        shape="rounded"
        size={buttonSize}
        onClick={cancelOnClick}
        disabled={isSaving}
        aria-label={cancelLabel}
        className={styles.cancel}
      >
        {cancelLabel}
      </Button>

      <RevelioTooltip label={saveTooltipLabel} offset={[saveTooltipOffset, 8]}>
        <span>
          <Button
            color="primary"
            shape="rounded"
            size={buttonSize}
            onClick={onSave}
            disabled={saveIsDisabled || isSaving}
            aria-label={saveLabel}
          >
            {isSaving ? 'Saving' : saveLabel}
          </Button>
        </span>
      </RevelioTooltip>
    </>
  );
};
